import { fetchAuthSession } from 'aws-amplify/auth';
import { facilityBookingApi } from '../FacilityBooking/facilityBookingApi';
import { getAllSites } from '../SiteManagement/siteApi';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';

/** Villa street blocks from GET /facility/blocks (`block_type: "street"`) */
export const BLOCK_TYPE_STREET = 'street';

export const normalizeBlockType = blockType =>
  String(blockType ?? '')
    .trim()
    .toLowerCase();

export const isStreetBlockType = blockType =>
  normalizeBlockType(blockType) === BLOCK_TYPE_STREET;

const extractArray = (data, keys) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    const candidate = data[key];
    if (Array.isArray(candidate)) return candidate;
  }
  if (data.data != null) {
    if (Array.isArray(data.data)) return data.data;
    return extractArray(data.data, keys);
  }
  return [];
};

const facilityGet = async endpoint => {
  const { tokens } = await fetchAuthSession();
  const token = tokens?.idToken?.toString();
  if (!token) {
    throw new Error('Authentication required. Please sign in again.');
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const err = await response.json();
      message = err.error || err.message || message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const parseBlockFloors = block => {
  const meta = block.block_metadata || block.metadata || {};
  let floorCount = Number(meta.floors);
  if (!Number.isFinite(floorCount) || floorCount < 1) {
    floorCount = 1;
  }

  const floorNames = Array.isArray(meta.floor_names) ? meta.floor_names : [];
  const floors = [];

  for (let i = 0; i < floorCount; i += 1) {
    const named = floorNames[i];
    floors.push({
      floor_no: i,
      name:
        named ||
        (i === 0 ? 'Ground Floor' : i === 1 ? 'Floor 1' : `Floor ${i}`),
    });
  }

  return floors;
};

const mapBlockFromApi = block => {
  const id = block.block_id || block.blockId || block.id;
  const name = block.block_name || block.blockName || block.name || id || 'Block';
  const block_type = normalizeBlockType(
    block.block_type || block.blockType || ''
  );
  if (!id) return null;
  return {
    id: String(id),
    name: String(name),
    block_type,
    floors: parseBlockFloors(block),
  };
};

export const normalizeSites = response => {
  const raw = Array.isArray(response)
    ? response
    : response?.sites || response?.data || response?.items || [];

  return raw
    .map(site => ({
      site_id: site.site_id || site.id,
      site_name: site.site_name || site.name || site.site_id || site.id,
    }))
    .filter(site => site.site_id);
};

export const fetchSitesForQr = async () => {
  const response = await getAllSites();
  return normalizeSites(response);
};

export const fetchAllBlocksForQr = async siteId => {
  if (!siteId) return [];

  const data = await facilityGet(
    `/facility/blocks?site_id=${encodeURIComponent(siteId)}`
  );
  const raw = extractArray(data, [
    'blocks',
    'block_list',
    'blockList',
    'results',
    'items',
  ]);

  return raw.map(mapBlockFromApi).filter(Boolean);
};

/** Tower / building blocks (excludes `block_type: street`) */
export const fetchBlocksForQr = async siteId => {
  const all = await fetchAllBlocksForQr(siteId);
  return all.filter(b => !isStreetBlockType(b.block_type));
};

/** Villa street blocks (`block_type: street`) — same API shape as blocks */
export const fetchStreetsForQr = async siteId => {
  const all = await fetchAllBlocksForQr(siteId);
  return all.filter(b => isStreetBlockType(b.block_type));
};

export const fetchAmenitiesForQr = async siteId => {
  if (!siteId) return [];

  const amenities = await facilityBookingApi.getAmenities(siteId);
  const list = Array.isArray(amenities) ? amenities : [];

  return list
    .map(amenity => {
      const id = amenity.amenity_id || amenity.id;
      const name =
        amenity.amenity_name ||
        amenity.name ||
        amenity.amenity_type ||
        id ||
        'Amenity';
      if (!id) return null;
      return { id: String(id), name: String(name) };
    })
    .filter(Boolean);
};

export const fetchSiteAreasForQr = async siteId => {
  const [allBlocks, amenities] = await Promise.all([
    fetchAllBlocksForQr(siteId),
    fetchAmenitiesForQr(siteId),
  ]);

  return {
    blocks: allBlocks.filter(b => !isStreetBlockType(b.block_type)),
    streets: allBlocks.filter(b => isStreetBlockType(b.block_type)),
    amenities,
  };
};

export const getSiteLabelFromList = (sites, siteId) => {
  const site = sites.find(s => s.site_id === siteId);
  return site?.site_name || siteId || '';
};
